/**
 * CaseStatusSidebar — right-panel status cards for CaseWorkspacePage.
 *
 * Renders 5 cards using existing API data passed as props:
 *   1. Readiness / completion
 *   2. Missing fields with "Fix in wizard" links
 *   3. Risk flags (evaluation warnings)
 *   4. Document checklist (static N-400 list cross-referenced with uploads)
 *   5. Payment / PDF status
 *
 * No new API calls — all data comes from the parent page queries.
 */
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { cases } from "@/lib/endpoints";
import { SECTION_TO_STEP } from "@/lib/caseConstants";
import type { CaseResponse, EvaluationResult, JobResponse, DocumentResponse } from "@/lib/api-types";
import type { ConfirmedExtraction } from "@/components/workspace/ChatUploadZone";

// Static N-400 document checklist.
// document_type values match what StepDocuments.tsx passes on upload.
const N400_CHECKLIST = [
  { label: "Permanent Resident Card (Green Card) — front & back", type: "green_card", required: true },
  { label: "Passport-style photos (2 copies)", type: "passport_photo", required: true },
  { label: "Tax returns — 3 most recent years", type: "other", required: false },
  { label: "Travel records (trips outside U.S.)", type: "other", required: false },
  { label: "Marriage certificate", type: "marriage_certificate", required: false },
  { label: "Divorce decree (for each prior marriage)", type: "divorce_decree", required: false },
  { label: "Name change documents (if applicable)", type: "name_change", required: false },
] as const;

interface Props {
  caseId: string;
  caseData: CaseResponse;
  evalData: EvaluationResult | undefined;
  evalLoading: boolean;
  jobsData: JobResponse[];
  docsData: DocumentResponse[];
  feeDollars: string;
  /** Session-only extractions confirmed during the upload flow (T4). */
  confirmedExtractions?: ConfirmedExtraction[];
}

export function CaseStatusSidebar({
  caseId,
  caseData,
  evalData,
  evalLoading,
  jobsData,
  docsData,
  feeDollars,
  confirmedExtractions = [],
}: Props) {
  const isPaid = !!caseData.paid_at;
  const isReady = evalData?.ready ?? false;
  const missingData = evalData?.missing_data ?? [];
  const warnings = evalData?.warnings ?? [];

  // Completion %: rough estimate — treat missing required items against a cap of 20.
  const TOTAL_REQUIRED = 20;
  const filledCount = Math.max(0, TOTAL_REQUIRED - missingData.length);
  const progressPct = evalLoading ? null : isReady ? 100 : Math.round((filledCount / TOTAL_REQUIRED) * 100);

  // Jobs: latest done job for PDF download.
  const latestDoneJob = jobsData.find((j) => j.state === "done");
  const hasRunningJob = jobsData.some((j) => j.state === "pending" || j.state === "running");

  // Docs: match uploaded types against checklist.
  const uploadedTypes = new Set(docsData.map((d) => d.document_type).filter(Boolean));

  // Required doc progress (green_card + passport_photo are the only required types).
  const requiredChecklist = N400_CHECKLIST.filter((item) => item.required);
  const uploadedRequiredCount = requiredChecklist.filter((item) => uploadedTypes.has(item.type)).length;

  const eligibilityStatus = caseData.eligibility_status;
  // Until chat-gating in /workspace stamps the case as eligible, the
  // wizard isn't usable (form-mapping evaluators assume a screened-in
  // case). Disable every "go fix this in the wizard" link in this
  // sidebar so the user finishes eligibility first; WizardLayout has
  // a redirect-back guard for direct URL navigation.
  const wizardOpen = eligibilityStatus === "eligible";
  const wizardDisabledTitle = "Finish the eligibility check before editing the form.";

  return (
    <div className="space-y-4">
      {/* 0 — Eligibility status card (T3) */}
      <EligibilityCard status={eligibilityStatus} />

      {/* 1 — Readiness card */}
      <SidebarCard>
        <CardHeader
          icon={
            isReady ? (
              <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )
          }
          title="Application readiness"
          badge={
            evalLoading ? "…" : isReady ? "Complete" : `${progressPct}%`
          }
          badgeColor={isReady ? "emerald" : "blue"}
        />

        {evalLoading ? (
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-slate-200 rounded-full animate-pulse" />
          </div>
        ) : (
          <>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={cn("h-full rounded-full transition-all duration-500", isReady ? "bg-emerald-500" : "bg-blue-500")}
                style={{ width: `${progressPct ?? 0}%` }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              {isReady
                ? "All required fields complete."
                : `${missingData.length} required field${missingData.length !== 1 ? "s" : ""} still needed.`}
            </p>
          </>
        )}
      </SidebarCard>

      {/* 2 — Missing fields card — always visible once eval settles */}
      {!evalLoading && (
        <SidebarCard>
          <CardHeader
            icon={
              missingData.length === 0 ? (
                <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              )
            }
            title="Missing information"
            badge={missingData.length === 0 ? "Complete ✓" : `${missingData.length}`}
            badgeColor={missingData.length === 0 ? "emerald" : "amber"}
          />
          {missingData.length === 0 ? (
            <p className="text-xs text-emerald-700 leading-relaxed">
              All required fields are complete.
            </p>
          ) : (
            <ul className="space-y-2">
              {missingData.map((issue) => {
                const step = (issue.section && SECTION_TO_STEP[issue.section]) ?? "personal";
                return (
                  <li key={issue.key} className="flex items-start justify-between gap-2 text-xs">
                    <div className="flex items-start gap-1.5 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 mt-1.5" />
                      <span className="text-slate-600 leading-relaxed">{issue.message}</span>
                    </div>
                    {wizardOpen ? (
                      <Link
                        to={`/cases/${caseId}/wizard/${step}`}
                        className="shrink-0 text-[10px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-md transition-colors whitespace-nowrap"
                      >
                        Fix →
                      </Link>
                    ) : (
                      <span
                        title={wizardDisabledTitle}
                        aria-disabled="true"
                        className="shrink-0 text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md whitespace-nowrap cursor-not-allowed"
                      >
                        Fix →
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </SidebarCard>
      )}

      {/* 3 — Risk flags card */}
      {!evalLoading && warnings.length > 0 && (
        <SidebarCard>
          <CardHeader
            icon={
              <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            }
            title="Risk flags"
            badge={`${warnings.length}`}
            badgeColor="orange"
          />
          <ul className="space-y-2">
            {warnings.map((w) => (
              <li key={w.key} className="flex items-start gap-1.5 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0 mt-1.5" />
                <span className="text-slate-600 leading-relaxed">{w.message}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-slate-400 leading-snug border-t border-slate-100 pt-3">
            Flags are informational. Review with an immigration attorney before filing.
          </p>
        </SidebarCard>
      )}

      {/* 4 — Document checklist card */}
      <SidebarCard>
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          title="Documents"
        />

        {/* Required-docs progress summary */}
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-xs text-slate-500">
            Required uploaded:
          </span>
          <span className={cn(
            "text-xs font-semibold tabular-nums",
            uploadedRequiredCount === requiredChecklist.length ? "text-emerald-600" : "text-amber-600",
          )}>
            {uploadedRequiredCount} / {requiredChecklist.length}
          </span>
        </div>

        {/* Extraction note — shown when user confirmed doc fields this session */}
        {confirmedExtractions.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {confirmedExtractions.length} field{confirmedExtractions.length !== 1 ? "s" : ""} noted from documents
          </div>
        )}

        <ul className="space-y-1.5">
          {N400_CHECKLIST.map((item) => {
            const uploaded =
              item.type !== "other"
                ? uploadedTypes.has(item.type)
                : false;
            return (
              <li key={item.label} className="flex items-start gap-2 text-xs">
                <div className={cn(
                  "w-4 h-4 rounded-full shrink-0 mt-0.5 flex items-center justify-center",
                  uploaded ? "bg-emerald-100" : "bg-slate-100",
                )}>
                  {uploaded ? (
                    <svg className="w-2.5 h-2.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  )}
                </div>
                <span className={cn(
                  "leading-relaxed",
                  uploaded ? "text-slate-400 line-through" : item.required ? "text-slate-700" : "text-slate-500",
                )}>
                  {item.label}
                  {item.required && !uploaded && (
                    <span className="ml-1 text-[10px] text-amber-500 font-medium">required</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        {wizardOpen ? (
          <Link
            to={`/cases/${caseId}/wizard/documents`}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload documents →
          </Link>
        ) : (
          <span
            title={wizardDisabledTitle}
            aria-disabled="true"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 cursor-not-allowed"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload documents →
          </span>
        )}
      </SidebarCard>

      {/* 5 — Payment / PDF card. Hidden until eligibility passes —
            the Pay/Generate buttons inside link to wizard routes that
            redirect back to the workspace pre-eligibility, which would
            confuse users. */}
      {wizardOpen && (
        <PaymentPdfCard
          caseId={caseId}
          isPaid={isPaid}
          isReady={isReady}
          feeDollars={feeDollars}
          latestDoneJob={latestDoneJob}
          hasRunningJob={hasRunningJob}
        />
      )}
    </div>
  );
}

// ─── Eligibility card (T3) ───────────────────────────────────────────────────

function EligibilityCard({ status }: { status?: string | null }) {
  if (!status) {
    return (
      <SidebarCard>
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          title="Eligibility"
          badge="Not checked"
          badgeColor="slate"
        />
        <p className="text-xs text-slate-400 leading-relaxed">
          Use the assistant to complete your eligibility check.
        </p>
      </SidebarCard>
    );
  }

  if (status === "eligible") {
    return (
      <SidebarCard highlight="emerald">
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          }
          title="Eligibility"
          badge="Eligible ✓"
          badgeColor="emerald"
        />
        <p className="text-xs text-emerald-700 leading-relaxed">
          You appear to qualify for naturalization.
        </p>
      </SidebarCard>
    );
  }

  if (status === "out_of_scope") {
    return (
      <SidebarCard>
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          }
          title="Eligibility"
          badge="Needs review"
          badgeColor="slate"
        />
        <p className="text-xs text-slate-500 leading-relaxed">
          Your answers flagged items that need attorney review. Your case is saved.
        </p>
      </SidebarCard>
    );
  }

  // manual_review
  return (
    <SidebarCard highlight="blue">
      <CardHeader
        icon={
          <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        }
        title="Eligibility"
        badge="Review needed"
        badgeColor="amber"
      />
      <p className="text-xs text-slate-500 leading-relaxed">
        Some answers need closer review. Consult an immigration attorney before filing.
      </p>
    </SidebarCard>
  );
}

// ─── Payment / PDF card ───────────────────────────────────────────────────────

function PaymentPdfCard({
  caseId,
  isPaid,
  isReady,
  feeDollars,
  latestDoneJob,
  hasRunningJob,
}: {
  caseId: string;
  isPaid: boolean;
  isReady: boolean;
  feeDollars: string;
  latestDoneJob: JobResponse | undefined;
  hasRunningJob: boolean;
}) {
  const downloadMut = useMutation({
    mutationFn: () => cases.jobDownload(caseId, latestDoneJob!.id),
    onSuccess: (data) => {
      window.open(data.download_url, "_blank", "noopener,noreferrer");
    },
  });

  if (!isPaid) {
    return (
      <SidebarCard highlight="blue">
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          }
          title="Unlock your draft"
        />
        <p className="text-xs text-slate-600 leading-relaxed mb-3">
          Pay once to download your complete N-400 draft with document checklist.
          USCIS filing fees are separate and paid directly to USCIS.
        </p>
        <div className="flex items-baseline gap-1.5 mb-3">
          <span className="text-2xl font-bold text-slate-900">${feeDollars}</span>
          <span className="text-xs text-slate-400">one-time</span>
        </div>
        <Link
          to={`/cases/${caseId}/wizard/checkout`}
          className="block w-full text-center text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl py-2.5 transition-colors"
        >
          Pay to download →
        </Link>
      </SidebarCard>
    );
  }

  if (latestDoneJob) {
    const fmtDate = latestDoneJob.completed_at
      ? new Date(latestDoneJob.completed_at).toLocaleString("en-US", {
          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        })
      : null;
    return (
      <SidebarCard highlight="emerald">
        <CardHeader
          icon={
            <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          }
          title="N-400 packet ready"
          badge="Ready"
          badgeColor="emerald"
        />
        {fmtDate && <p className="text-xs text-slate-400 mb-3">Generated {fmtDate}</p>}
        <button
          onClick={() => downloadMut.mutate()}
          disabled={downloadMut.isPending}
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl py-2.5 transition-colors disabled:opacity-60"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {downloadMut.isPending ? "Preparing…" : "Download PDF"}
        </button>
        {downloadMut.isError && (
          <p className="mt-2 text-xs text-red-600">Download failed. Please try again.</p>
        )}
      </SidebarCard>
    );
  }

  if (hasRunningJob) {
    return (
      <SidebarCard>
        <CardHeader
          icon={
            <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          }
          title="Generating PDF"
        />
        <p className="text-xs text-slate-500 leading-relaxed">
          Your N-400 packet is being generated. This usually takes under a minute.
        </p>
      </SidebarCard>
    );
  }

  // Paid but no job yet — prompt to generate.
  return (
    <SidebarCard highlight="blue">
      <CardHeader
        icon={
          <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        }
        title="Fee paid"
        badge="Paid"
        badgeColor="emerald"
      />
      <p className="text-xs text-slate-600 leading-relaxed mb-3">
        {isReady
          ? "Your application is complete. Generate your PDF packet to download."
          : "You've paid. Complete the remaining fields, then generate your packet."}
      </p>
      <Link
        to={`/cases/${caseId}/wizard/review`}
        className="block w-full text-center text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl py-2.5 transition-colors"
      >
        {isReady ? "Generate packet →" : "Continue application →"}
      </Link>
    </SidebarCard>
  );
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function SidebarCard({
  children,
  highlight,
}: {
  children: React.ReactNode;
  highlight?: "blue" | "emerald";
}) {
  return (
    <div className={cn(
      "bg-white rounded-2xl border p-4 shadow-sm",
      highlight === "blue" && "border-blue-200 bg-blue-50/30",
      highlight === "emerald" && "border-emerald-200 bg-emerald-50/20",
      !highlight && "border-slate-200/80",
    )}>
      {children}
    </div>
  );
}

function CardHeader({
  icon,
  title,
  badge,
  badgeColor = "slate",
}: {
  icon: React.ReactNode;
  title: string;
  badge?: string;
  badgeColor?: "slate" | "blue" | "emerald" | "amber" | "orange";
}) {
  const badgeClasses: Record<string, string> = {
    slate: "bg-slate-100 text-slate-500",
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    orange: "bg-orange-100 text-orange-700",
  };

  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="shrink-0">{icon}</div>
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{title}</span>
      </div>
      {badge && (
        <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded-full", badgeClasses[badgeColor])}>
          {badge}
        </span>
      )}
    </div>
  );
}
